import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";

import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable";
import { Notice, NoticeList, NoticeText } from "components/ui/Notice";
import { PageHeader } from "components/ui/PageHeader";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { useCreateStripeAccount } from "features/stripe/hooks/useCreateStripeAccount";
import { useStripeDashboardLink } from "features/stripe/hooks/useStripeDashboardLink";
import { useStripeOnboardingLink } from "features/stripe/hooks/useStripeOnboardingLink";
import { useStripePayments } from "features/stripe/hooks/useStripePayments";
import { useStripeStatus } from "features/stripe/hooks/useStripeStatus";
import { pathGenerators } from "utils/routes";
import { showMutationError } from "utils/showMutationError";

import styles from "./PaymentDetailsPage.module.css";

const ONBOARDING_STATUS_PILL = {
  NOT_STARTED: PILL_COLORS.statusNoStatus,
  IN_PROGRESS: PILL_COLORS.statusPending,
  RESTRICTED: PILL_COLORS.statusWarning,
  ACTIVE: PILL_COLORS.statusSuccess,
};

const ONBOARDING_STATUS_LABEL = {
  NOT_STARTED: "Not started",
  IN_PROGRESS: "In progress",
  RESTRICTED: "Restricted",
  ACTIVE: "Active",
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = moment(value, moment.ISO_8601);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY HH:mm") : value;
};

const formatAmount = (amountCents, currency) => {
  if (amountCents === null || amountCents === undefined) return "-";
  try {
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency: currency || "EUR",
    }).format(amountCents / 100);
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency || ""}`.trim();
  }
};

const getPaymentStatusPill = (status) => {
  const normalized = (status || "").toLowerCase();
  if (normalized.includes("fail")) return PILL_COLORS.statusError;
  if (normalized.includes("pend")) return PILL_COLORS.statusPending;
  if (
    normalized.includes("succe") ||
    normalized.includes("paid") ||
    normalized.includes("complete")
  ) {
    return PILL_COLORS.statusSuccess;
  }
  return PILL_COLORS.statusNoStatus;
};

const isConflictError = (error) =>
  error?.response?.status === 409 || error?.code === "CONFLICT";

const PAYMENT_COLUMNS = [
  { id: "created", header: "Date", render: (p) => formatDateTime(p.created) },
  {
    id: "amount",
    header: "Amount",
    render: (p) => (
      <strong>{formatAmount(p.amountCents, p.currency)}</strong>
    ),
  },
  {
    id: "status",
    header: "Status",
    render: (p) => (
      <Pill {...getPaymentStatusPill(p.status)}>{p.status || "-"}</Pill>
    ),
  },
  {
    id: "consultationPaymentDueId",
    header: "Consultation Ref",
    render: (p) => p.consultationPaymentDueId ?? "-",
  },
  {
    id: "patientId",
    header: "Patient Ref",
    render: (p) => p.patientId ?? "-",
  },
];

/*
 * The practice's Stripe connection, as one box.
 *
 * Every state of it — still loading, failed to load, not connected, part-way through
 * onboarding, live — is the same question answered differently: can this practice take
 * card payments, and what is the next thing to do about it. Four loose blocks answered
 * that in four visual languages, and the loading case answered it with a centred grey
 * line that looked like a different screen entirely.
 *
 * The action lives in the notice's own slot, so the box always carries both the state
 * and the way to act on it.
 */
const StripeConnection = ({
  status,
  isLoadingStatus,
  isStatusError,
  onRetryStatus,
  isConnecting,
  onConnect,
  isResuming,
  onResumeOnboarding,
  isOpeningDashboard,
  isDashboardUnavailable,
  onOpenDashboard,
}) => {
  if (isLoadingStatus) {
    return (
      <Notice tone="info" testId="stripe-connection-notice">
        <NoticeText>Checking this practice&apos;s Stripe connection...</NoticeText>
      </Notice>
    );
  }

  /*
   * A failed status request is not "not connected", though it used to render as it:
   * `isNotConnected` reads `!status`, so a dropped request invited the admin to connect
   * an account that may already exist. Red rather than blue, because the honest answer
   * is that we do not know whether this practice can charge anyone.
   */
  if (isStatusError) {
    return (
      <Notice
        tone="blocking"
        testId="stripe-connection-notice"
        action={
          <Button variant="outline" onClick={onRetryStatus}>
            Try again
          </Button>
        }
      >
        <NoticeText>
          The Stripe connection status could not be loaded, so it is not known
          whether this practice can currently take card payments.
        </NoticeText>
      </Notice>
    );
  }

  const onboardingStatus = status?.onboardingStatus || "NOT_STARTED";
  const isNotConnected = !status || onboardingStatus === "NOT_STARTED";
  const isFullyActive =
    onboardingStatus === "ACTIVE" && !!status?.chargesEnabled;

  if (isNotConnected) {
    return (
      <Notice
        tone="info"
        testId="stripe-connection-notice"
        action={
          <Button onClick={onConnect} disabled={isConnecting}>
            {isConnecting ? "Connecting..." : "Connect with Stripe"}
          </Button>
        }
      >
        <NoticeText>
          Connect your practice&apos;s Stripe account to accept card payments
          from patients. Kinesin takes a small per-transaction platform fee;
          your practice remains the merchant of record and is responsible for
          its own Stripe fees and disputes.
        </NoticeText>
      </Notice>
    );
  }

  if (!isFullyActive) {
    const outstanding = Array.isArray(status?.currentlyDue)
      ? status.currentlyDue
      : [];

    return (
      <Notice
        tone="info"
        testId="stripe-connection-notice"
        isStacked
        action={
          <Button onClick={onResumeOnboarding} disabled={isResuming}>
            {isResuming ? "Preparing..." : "Continue onboarding"}
          </Button>
        }
      >
        <div className={styles.noticeStatus}>
          <Pill
            {...(ONBOARDING_STATUS_PILL[onboardingStatus] ||
              PILL_COLORS.statusNoStatus)}
          >
            {ONBOARDING_STATUS_LABEL[onboardingStatus] || onboardingStatus}
          </Pill>
        </div>

        {!!status?.disabledReason && (
          <NoticeText>Reason: {status.disabledReason}</NoticeText>
        )}

        {outstanding.length > 0 && (
          <>
            <NoticeText isLead>
              Stripe requires the following before charges can be enabled:
            </NoticeText>
            <NoticeList>
              {outstanding.map((requirement) => (
                <li key={requirement} className={styles.dueListItem}>
                  {requirement}
                </li>
              ))}
            </NoticeList>
          </>
        )}
      </Notice>
    );
  }

  return (
    <Notice
      tone="info"
      testId="stripe-connection-notice"
      isStacked
      action={
        <Button
          variant="outline"
          onClick={onOpenDashboard}
          disabled={isOpeningDashboard || isDashboardUnavailable}
        >
          {isOpeningDashboard ? "Opening..." : "Manage in Stripe Dashboard"}
        </Button>
      }
    >
      <div className={styles.noticeStatus}>
        <Pill {...PILL_COLORS.statusSuccess}>Active</Pill>
        <span className={styles.noticeStatusText}>
          This practice can take card payments.
        </span>
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Charges Enabled</span>
          <span className={styles.summaryValue}>
            {status.chargesEnabled ? "Yes" : "No"}
          </span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Payouts Enabled</span>
          <span className={styles.summaryValue}>
            {status.payoutsEnabled ? "Yes" : "No"}
          </span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>Details Submitted</span>
          <span className={styles.summaryValue}>
            {status.detailsSubmitted ? "Yes" : "No"}
          </span>
        </div>
      </div>

      {!!isDashboardUnavailable && (
        <NoticeText>
          A Stripe Dashboard login link isn&apos;t available for this account
          configuration.
        </NoticeText>
      )}
    </Notice>
  );
};

const PaymentDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [dashboardUnavailable, setDashboardUnavailable] = useState(false);

  const {
    data: status,
    isLoading: isLoadingStatus,
    isError: isStatusError,
    refetch: refetchStatus,
  } = useStripeStatus();

  const { data: paymentsData, isLoading: isLoadingPayments } =
    useStripePayments({ page: currentPage, size: rowsPerPage });

  const { mutateAsync: createAccount, isPending: isCreatingAccount } =
    useCreateStripeAccount();
  const { mutateAsync: mintOnboardingLink, isPending: isMintingLink } =
    useStripeOnboardingLink();
  const { mutateAsync: mintDashboardLink, isPending: isMintingDashboardLink } =
    useStripeDashboardLink();

  // Handle the Stripe-hosted onboarding return/refresh redirect targets —
  // same component, distinguished only by the pathname suffix. Refetch status,
  // toast, and hop back to the canonical /settings/payments-details URL so the
  // sub-path doesn't linger in the address bar or browser history.
  const hasHandledRedirectRef = useRef(false);
  useEffect(() => {
    if (hasHandledRedirectRef.current) return;

    const isOnboardingComplete = location.pathname.endsWith(
      "/onboarding-complete"
    );
    const isOnboardingRefresh = location.pathname.endsWith(
      "/onboarding-refresh"
    );
    if (!isOnboardingComplete && !isOnboardingRefresh) return;

    hasHandledRedirectRef.current = true;

    if (isOnboardingComplete) {
      toast.success("Stripe onboarding complete — syncing status");
    } else {
      toast("Onboarding was not completed, you can resume below");
    }

    refetchStatus();
    navigate(pathGenerators.settings.paymentsDetails(), { replace: true });
  }, [location.pathname, navigate, refetchStatus]);

  const handleConnect = async () => {
    try {
      await createAccount();
    } catch (error) {
      if (isConflictError(error)) {
        toast("A Stripe account already exists — refreshing status.");
        refetchStatus();
        return;
      }
      showMutationError(error, "Failed to create Stripe account");
      return;
    }

    try {
      const link = await mintOnboardingLink();
      window.location.href = link.url;
    } catch (error) {
      showMutationError(error, "Failed to get Stripe onboarding link");
    }
  };

  const handleResumeOnboarding = async () => {
    try {
      const link = await mintOnboardingLink();
      window.location.href = link.url;
    } catch (error) {
      showMutationError(error, "Failed to get Stripe onboarding link");
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const link = await mintDashboardLink();
      window.open(link.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      if (isConflictError(error)) {
        setDashboardUnavailable(true);
        return;
      }
      showMutationError(error, "Failed to open Stripe dashboard");
    }
  };

  const payments = paymentsData?.data || [];
  const totalRecords = paymentsData?.totalRecords || 0;

  return (
    <div className={styles.content}>
      <PageHeader title="Payment Details" />

      <section className={styles.section}>
        <StripeConnection
          status={status}
          isLoadingStatus={isLoadingStatus}
          isStatusError={isStatusError}
          onRetryStatus={refetchStatus}
          isConnecting={isCreatingAccount || isMintingLink}
          onConnect={handleConnect}
          isResuming={isMintingLink}
          onResumeOnboarding={handleResumeOnboarding}
          isOpeningDashboard={isMintingDashboardLink}
          isDashboardUnavailable={dashboardUnavailable}
          onOpenDashboard={handleOpenDashboard}
        />
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h4 className={styles.sectionTitle}>Recent Payments</h4>
        </div>
        <DataTable
          columns={PAYMENT_COLUMNS}
          data={payments}
          isLoading={isLoadingPayments}
          currentPage={currentPage}
          totalCount={totalRecords}
          rowsPerPage={rowsPerPage}
          rowsOptions={[10, 20, 50, 100]}
          onPageChange={setCurrentPage}
          onRowsChange={(rows) => {
            setRowsPerPage(rows);
            setCurrentPage(1);
          }}
          emptyTitle="No payments yet"
          emptyText="Payments taken through this practice will appear here."
        />
      </section>
    </div>
  );
};

export { PaymentDetailsPage };
